/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'

import { Input, InputGroup, Spinner } from '@components'
import Button from '@components/Button'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { UpdateDisciplineRequest } from '@models/discipline.model'
import { addDisciplineSchema } from '@schemas/addDiscipline.schema'
import { disciplineApi } from '@services/DisciplineApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = UpdateDisciplineRequest

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function EditDiscipline() {
	const { modalInfo: id, modalName } = useAppSelector((state) => state.modal)

	const dispatch = useAppDispatch()

	const [getDiscipline, { data: discipline, isLoading: loadingGetDiscipline }] =
		disciplineApi.useShowMutation()

	const [editDiscipline, { isLoading, isSuccess }] =
		disciplineApi.useEditMutation()

	const {
		register,
		handleSubmit,
		formState: { errors, touchedFields },
		setValue,
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addDisciplineSchema),
	})

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		editDiscipline({
			...data,
			id: id as number,
		})
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.updatedDiscipline))
		}
	}, [isSuccess, dispatch])

	useEffect(() => {
		if (id && modalName === 'editDiscipline') {
			getDiscipline({
				id: Number(id),
			})
		}
	}, [id, modalName, getDiscipline])

	useEffect(() => {
		if (discipline) {
			setValue('name', discipline?.name as string)
		}
	}, [discipline, setValue])

	if (loadingGetDiscipline) {
		return (
			<Modal name='updateLiveClass'>
				<Dialog.Panel
					style={getCssStyle(
						{ name: 'overflowY', value: 'auto' },
						{ name: 'maxWidth', value: pixelsToRem(630) },
						{ name: 'maxHeight', value: pixelsToRem(600) },
						{ name: 'width', value: '100%' }
					)}
					className={`${styles.container} modal-scroll`}
				>
					<div className='w-full h-[90%] flex items-center justify-center mt-10'>
						<Spinner size='xl' />
					</div>
				</Dialog.Panel>
			</Modal>
		)
	}

	return (
		<Modal name='editDiscipline'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Editar Disciplina</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para editar a disciplina
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup className='mt-4' label='Nome da disciplina' id='name'>
						<Input
							isValid={inputIsValid(errors.name, touchedFields.name)}
							{...register('name')}
							type='text'
						/>
					</InputGroup>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Editar Disciplina
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
